import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EmptyState from './emptyState';
import './css/myRequestsList.css';
import { formatDateToDDMMYYYY } from '../../helper-functions';
import { Drawer } from '@mui/material';
import { post } from '../../config/api';
import { API_ENDPOINTS } from '../../config/apiEndpoints';

interface CertificateCardProps {
  id: string;
  title: string;
  status: string;
  number: string;
  date: string;
  deliveryCode?: string;
  message: string;
  onClick: () => void;
  currentStatus: string;
  unit?: string;
  requestId: string;
  isDelivered?: boolean;
  showSupport?: boolean;
  onRefresh?: () => void;
}

const CertificateCard: React.FC<CertificateCardProps> = ({ id, title, status, number, date, deliveryCode, message, onClick, currentStatus, unit, requestId, isDelivered, showSupport, onRefresh }) => {
  const [showReceivedModal, setShowReceivedModal] = useState(false);
  const [showNotReceivedModal, setShowNotReceivedModal] = useState(false);

  const handleYesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowReceivedModal(true);
  };

  const handleNoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowNotReceivedModal(true);
  };

  const handleConfirmReceived = async () => {
    try {
      await post(API_ENDPOINTS.UPDATE_HANDOVER_STATUS(requestId, 'yes'), {});
      onRefresh?.();
    } catch (error) {
      console.error('Error confirming certificate received:', error);
    }
    setShowReceivedModal(false);
  };

  const handleConfirmNotReceived = async () => {
    try {
      await post(API_ENDPOINTS.UPDATE_HANDOVER_STATUS(requestId, 'no'), {});
      onRefresh?.();
    } catch (error) {
      console.error('Error confirming certificate not received:', error);
    }
    setShowNotReceivedModal(false);
  };

  return (
    <>
      <div className="certificate-card" onClick={onClick} style={{ cursor: 'pointer' }}>
        <div style={{ padding: 16 }}>
          <div className="card-header">
            <div className="card-title">{title}</div>
            <div className={`status ${(status === 'Pending Delivery' || status.toLocaleLowerCase() === 'pending')
                ? 'pending'
                : status.toLocaleLowerCase() === 'rejected'
                  ? 'rejected'
                  : status.toLowerCase()
              }`}>
              {status.toLowerCase()==='pending'? 'In Progress': (status.toLowerCase()==='approved'? 'Pending Delivery': status)}
            </div>
          </div>
          {(currentStatus.toUpperCase() === 'ACTIVE'|| currentStatus.toUpperCase() === 'CANCELLED' || currentStatus.toUpperCase() === 'PENDING DELIVERY') ? <div className="card-subheader">
            No: {number} • {formatDateToDDMMYYYY(date)}
          </div> : <div className="card-subheader">
            Request ID: {id}
          </div>}

          <div className="card-message">
            {message}
          </div>
        </div>
        {unit !== 'IN57' && deliveryCode && deliveryCode.split('').length > 0 && (
          <div className="delivery-section" style={{borderBottom:'none'}}>
            <div className="delivery-label">Delivery code</div>
            <div className="delivery-code">
              {deliveryCode.split('').map((digit, idx) => (
                <div className="code-box" key={idx}>{digit}</div>
              ))}
            </div>
          </div>
        )}
        {currentStatus === 'Pending Delivery' && isDelivered === true && (
          <div className="received-confirmation-section">
            <span className="received-confirmation-text">Received the certificate?</span>
            <div className="received-confirmation-buttons">
              <button className="received-btn-yes" onClick={handleYesClick}>Yes</button>
              <button className="received-btn-no" onClick={handleNoClick}>No</button>
            </div>
          </div>
        )}
        {showSupport && (
          <div className="support-section">
            <p className="support-reported-text">You've reported your certificate as not delivered.</p>
            <div className="support-box">
              <p className="support-box-text">Need help? Reach out to Bayer customer care for further assistance.</p>
              <a href="tel:9418132222" className="support-phone-link" onClick={(e) => e.stopPropagation()}>
                <span className="support-phone-icon">&#9742;</span> 9418132222
              </a>
            </div>
          </div>
        )}
      </div>

      <Drawer anchor="bottom" open={showReceivedModal} onClose={() => setShowReceivedModal(false)}>
        <div className="confirmation-drawer">
          <h3 className="confirmation-drawer-title">Have you received the physical copy of your certificate?</h3>
          <div className="confirmation-drawer-buttons">
            <button className="confirmation-btn-back" onClick={() => setShowReceivedModal(false)}>
              &#8249; Go Back
            </button>
            <button className="confirmation-btn-action" onClick={handleConfirmReceived}>
              Received
            </button>
          </div>
        </div>
      </Drawer>

      <Drawer anchor="bottom" open={showNotReceivedModal} onClose={() => setShowNotReceivedModal(false)}>
        <div className="confirmation-drawer">
          <h3 className="confirmation-drawer-title">Are you sure you haven't received it yet?</h3>
          <div className="confirmation-drawer-buttons">
            <button className="confirmation-btn-back" onClick={() => setShowNotReceivedModal(false)}>
              &#8249; Go Back
            </button>
            <button className="confirmation-btn-action" onClick={handleConfirmNotReceived}>
              Not Received
            </button>
          </div>
        </div>
      </Drawer>
    </>
  );
};


interface Request {
  request_id: string;
  products: string[];
  license_number: string;
  license_expiry_date: string;
  deliveryCode?: string;
  status: string;
}

interface MyRequestsListProps {
  requests: Request[];
  onRefresh?: () => void;
}

const MyRequestsList: React.FC<MyRequestsListProps> = ({ requests, onRefresh }) => {
  const navigate = useNavigate();

  const renderCards = () => {
    return requests.map((req: any) => {
      const currentStatus: string = req.status;
      const title = `Principal Certificate - Crop Protection`;
      const message = req.message;
      const deliveryCode = req.delivery_code;

      const handleCardClick = () => {
        navigate('/summary', { state: { request: req } });
      };

      return (
        <CertificateCard
          key={req.request_id}
          id={req.request_id}
          title={title}
          status={currentStatus}
          number={req.certificate?.certificate_number || ''}
          date={req.certificate?.expiry_date  || ''}
          deliveryCode={deliveryCode}
          message={message}
          onClick={handleCardClick}
          currentStatus={currentStatus}
          unit={req.unit}
          requestId={req.request_id}
          isDelivered={req.is_delivered}
          showSupport={req.show_support}
          onRefresh={onRefresh}
        />
      );
    });
  };
 const hasPendingRequests = requests.some(request => (request.status.toLowerCase() === 'pending approval')|| (request.status.toLowerCase() === 'pending delivery')|| request.status.toLowerCase() === 'active');

  return (
    <div className="requests-list" style={{marginTop: !hasPendingRequests? 16:0}}>
      <h2 className="requests-list-title">My Requests</h2>
      {requests.length === 0 ? (
        <EmptyState
          message="No requests yet"
          subMessage="Use the ‘Request Now’ button to raise a new certificate request"
        />
      ) : (
        <div>
          {renderCards()}
        </div>
      )}
    </div>
  );
};

export default MyRequestsList;
